import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { RiSendPlaneFill } from "react-icons/ri";
import { BsSendXFill } from "react-icons/bs";

import themeClasses from "../themeClasses";
import UserInfoForm from "./modalContent/UserInfoForm";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { IoChatbubbles } from "react-icons/io5";

import { MdKeyboardVoice } from "react-icons/md";
import { MdOutlineKeyboardVoice } from "react-icons/md";

export default function MbChat({
  chatId,
  initialContent = [],
  onUpdateContent,
  onNewChat,
  currentTheme,
}) {
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem(`mbChatHistory_${chatId}`);
    return saved ? JSON.parse(saved) : initialContent;
  });
  const [input, setInput] = useState("");
  const [isBotResponding, setIsBotResponding] = useState(false);
  const [showWelcome, setShowWelcome] = useState(messages.length === 0);
  const [socketReady, setSocketReady] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stopBotResponse, setStopBotResponse] = useState(false);

  const [showRepresentativeButton, setShowRepresentativeButton] =
    useState(false);

  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const lastUserQuestion = useRef("");

  useEffect(() => {
    const saved = localStorage.getItem(`mbChatHistory_${chatId}`);
    setMessages(saved ? JSON.parse(saved) : []);
    setShowWelcome(!saved || JSON.parse(saved).length === 0);
  }, [chatId]);

  useEffect(() => {
    localStorage.setItem(`mbChatHistory_${chatId}`, JSON.stringify(messages));
    if (onUpdateContent) onUpdateContent(messages);
  }, [messages, chatId, onUpdateContent]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const websocketUrl = process.env.REACT_APP_WEBSOCKET_URL;
    if (!websocketUrl) return;
    const ws = new WebSocket(websocketUrl);
    socketRef.current = ws;

    ws.onopen = () => setSocketReady(true);
    ws.onclose = () => setSocketReady(false);

    ws.onmessage = (event) => {
      if (stopBotResponse) return;

      try {
        const data = JSON.parse(event.data);
        if (data.replay) {
          let text = data.replay;
          if (text.includes("[DONE]")) {
            setIsBotResponding(false);
            removeTypingIndicator();
            setShowRepresentativeButton(true);
            localStorage.setItem(
              `mbShowRepresentativeButton_${chatId}`,
              "true"
            );
          } else {
            appendBotMessage({
              title: lastUserQuestion.current || "Response",
              body: text,
            });
          }
        }
      } catch (err) {
        console.error(err);
        appendBotMessage({ title: "Error", body: "❌ Invalid response" });
        setIsBotResponding(false);
        removeTypingIndicator();
      }
    };

    return () => ws.close();
  }, [stopBotResponse]);

  const showTypingIndicator = () =>
    setMessages((prev) => [...prev, { sender: "bot", isTyping: true }]);

  const removeTypingIndicator = () =>
    setMessages((prev) => prev.filter((m) => !m.isTyping));

  const appendBotMessage = (content) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.sender === "bot" && last.title === content.title) {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...last,
          body: (last.body || "") + content.body,
        };
        return updated;
      }
      return [
        ...prev,
        { sender: "bot", title: content.title, body: content.body },
      ];
    });
  };

  const sendMessage = () => {
    if (!input.trim() || isBotResponding || !socketRef.current) return;

    setStopBotResponse(false);
    if (showWelcome) setShowWelcome(false);

    const userText = input.trim();
    lastUserQuestion.current = userText;
    setMessages((prev) => [...prev, { sender: "user", text: userText }]);
    setInput("");
    setIsBotResponding(true);
    showTypingIndicator();
    socketRef.current.send(JSON.stringify({ query: userText }));
  };

  const startNewChat = () => {
    const newId = Date.now().toString();
    localStorage.setItem(`mbChatHistory_${newId}`, JSON.stringify([]));
    setMessages([]);
    setShowWelcome(true);
    if (onNewChat) onNewChat(newId);
  };

  useEffect(() => {
    const savedMessages = localStorage.getItem(`mbChatHistory_${chatId}`);
    setMessages(savedMessages ? JSON.parse(savedMessages) : []);
    setShowWelcome(!savedMessages || JSON.parse(savedMessages).length === 0);

    const saved = localStorage.getItem(`mbShowRepresentativeButton_${chatId}`);
    setShowRepresentativeButton(saved === "true");
  }, [chatId]);

  const trendingTopics = [
    {
      topic: "AI in Fashion",
      description: "Artificial intelligence applied to fashion design",
    },
    {
      topic: "Sustainable Fabrics",
      description: "Eco-friendly and renewable textile materials",
    },
    {
      topic: "Smart Textiles",
      description: "Clothing integrated with sensors or electronics",
    },
    {
      topic: "3D Printed Clothing",
      description: "Customized garments printed layer by layer",
    },
    {
      topic: "Augmented Reality Try-On",
      description: "Virtual fitting rooms using AR technology",
    },
    {
      topic: "Eco-Friendly Dyes",
      description: "Non-toxic dyes reducing environmental impact",
    },
    {
      topic: "Wearable Tech",
      description: "Devices embedded in garments or accessories",
    },
    {
      topic: "Virtual Fashion Shows",
      description: "Online runway shows and digital events",
    },
    {
      topic: "Recycled Materials",
      description: "Fashion made from recycled textiles",
    },
    {
      topic: "Fashion NFTs",
      description: "Digital fashion collectibles on blockchain",
    },
  ];

  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef("");

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn("Speech recognition not supported");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = false;

    recognition.onstart = () => {
      setListening(true);
      finalTranscriptRef.current = "";
    };

    recognition.onresult = (event) => {
      let interim = "";
      let final = "";

      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) final += transcript;
        else interim += transcript;
      }

      setInput(interim || final);

      if (final.trim()) {
        finalTranscriptRef.current = final.trim();
      }
    };

    recognition.onend = () => {
      setListening(false);

      const text = finalTranscriptRef.current;

      if (text.length > 0) {
        setInput(text);

        setTimeout(() => {
          sendMessage();
        }, 250);
      }
    };

    recognitionRef.current = recognition;
  }, []);

  const handleVoiceRecording = () => {
    if (!recognitionRef.current) {
      alert("Speech recognition not supported in your browser.");
      return;
    }

    if (!listening) {
      recognitionRef.current.start();
    } else {
      recognitionRef.current.stop();
    }
  };

  return (
    <div className="mb-inner-chatbot h-full overflow-y-auto">
      <div className="chatbotInnerContent max-w-[800px] mx-auto">
        {showWelcome && (
          <div className="mb-welcome-screen absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center w-full max-w-[800px]">
            <h3 className="mb-5 text-3xl font-medium">
              Welcome to Merchant Bay! How can I assist you today?
            </h3>

            <div
              className={`mbBottonWrap border ${themeClasses[currentTheme].mbBottonWrap} flex mt-4 mx-auto rounded-[20px] h-[50px] w-full max-w-[800px]`}
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder={
                  socketReady ? "Type your message..." : "Connecting..."
                }
                disabled={!socketReady || isBotResponding}
                className={`${themeClasses[currentTheme].chatInputBox} flex-1 px-5 rounded-l-[20px] border-none shadow-none focus:outline-none focus:ring-0`}
              />

              <button
                className={`voiceBtn ${themeClasses[currentTheme].voiceBtn} px-4 py-1 cursor-pointer  `}
                onClick={() => {
                  if (!recognitionRef.current || isBotResponding) return;

                  if (!listening) {
                    recognitionRef.current.start();
                    setListening(true);
                  } else {
                    recognitionRef.current.stop();
                    setListening(false);
                  }
                }}
              >
                {isBotResponding || listening ? (
                  <MdOutlineKeyboardVoice
                    size={25}
                    className={`${
                      listening
                        ? `${themeClasses[currentTheme].voiceIconListening} animate-pulse`
                        : themeClasses[currentTheme].voiceIconInactive
                    }`}
                  />
                ) : (
                  <MdKeyboardVoice
                    size={25}
                    className={themeClasses[currentTheme].voiceIcon}
                  />
                )}
              </button>
              <button
                className={`mb-send-button w-[70px] flex items-center justify-center text-[25px] bg-[#54a958] text-white rounded-r-[20px] border-none cursor-pointer ${
                  isBotResponding ? "sending-active" : ""
                }`}
                onClick={sendMessage}
                disabled={!socketReady || isBotResponding}
              >
                {isBotResponding ? <BsSendXFill /> : <RiSendPlaneFill />}
              </button>
            </div>

            <div className="relatedInfoBox mt-5">
              <ul className="flex flex-wrap gap-2.5 justify-between items-stretch">
                {trendingTopics.map((item, idx) => (
                  <li
                    key={idx}
                    className={`${themeClasses[currentTheme].relatedInfoBox} border p-2.5 rounded flex items-center w-[19%] max-w-full text-sm`}
                  >
                    <div className="relInfo w-full">
                      <strong>{item.topic}:</strong> {item.description}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {messages.map((msg, idx) => {
          const isUser = msg.sender === "user";

          const isLastBotMessage =
            msg.sender === "bot" &&
            !msg.isTyping &&
            showRepresentativeButton &&
            idx === messages.length - 1;

          return (
            <div
              key={idx}
              className={`mbbot-message-${msg.sender} flex ${
                isUser ? "justify-end" : "justify-start"
              } mb-2`}
            >
              {isUser ? (
                <span className="bg-[#54a958] text-white px-3 py-2 rounded-[12px] max-w-[80%] inline-block text-right mb-2">
                  {msg.text}
                </span>
              ) : (
                <>
                  {msg.isTyping ? (
                    <div
                      className={`flex gap-2 items-center bg-gray-100 px-3 py-2 rounded-[12px] border ${themeClasses[currentTheme].botAnalyzing}`}
                    >
                      <span>Analyzing</span>
                      <span className="typing-dots flex gap-1 ">
                        {[...Array(3)].map((_, i) => (
                          <span
                            key={i}
                            className={`w-[5px] h-[5px] ${themeClasses[currentTheme].botAnalyzingDots} rounded-full inline-block animate-bounce`}
                            style={{ animationDelay: `${i * 0.2}s` }}
                          />
                        ))}
                      </span>
                    </div>
                  ) : (
                    <AccordionBotMessage
                      title={msg.title}
                      body={msg.body || msg.text}
                      isTyping={false}
                      currentTheme={currentTheme}
                      setIsModalOpen={setIsModalOpen}
                      showRepresentativeButton={isLastBotMessage}
                    />
                  )}
                </>
              )}
            </div>
          );
        })}

        {!showWelcome && (
          <div
            className={`mbBottonWrap border ${themeClasses[currentTheme].mbBottonWrap} flex bg-white absolute bottom-2.5 rounded-[20px] h-[50px] w-full max-w-[800px]`}
            style={{ display: "flex" }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder={
                socketReady ? "Type your message..." : "Connecting..."
              }
              disabled={isBotResponding || !socketReady}
              className={`${themeClasses[currentTheme].chatInputBox} flex-1 px-5 rounded-l-[20px] border-none shadow-none focus:outline-none focus:ring-0`}
            />
            <button
              className={`voiceBtn ${themeClasses[currentTheme].voiceBtn} px-4 py-1 cursor-pointer  `}
              onClick={() => {
                if (!recognitionRef.current || isBotResponding) return;

                if (!listening) {
                  recognitionRef.current.start();
                  setListening(true);
                } else {
                  recognitionRef.current.stop();
                  setListening(false);
                }
              }}
            >
              {isBotResponding || listening ? (
                <MdOutlineKeyboardVoice
                  size={25}
                  className={`${
                    listening
                      ? `${themeClasses[currentTheme].voiceIconListening} animate-pulse`
                      : themeClasses[currentTheme].voiceIconInactive
                  }`}
                />
              ) : (
                <MdKeyboardVoice
                  size={25}
                  className={themeClasses[currentTheme].voiceIcon}
                />
              )}
            </button>

            <button
              className={`mb-send-button w-[70px] flex items-center justify-center text-[25px] ${
                isBotResponding ? "bg-[#95eb9a]" : "bg-[#54a958]"
              } text-white rounded-r-[20px] border-none cursor-pointer ${
                isBotResponding ? "sending-active" : ""
              }`}
              onClick={() => {
                if (isBotResponding) {
                  setStopBotResponse(true);
                  setIsBotResponding(false);
                  removeTypingIndicator();
                } else {
                  sendMessage();
                }
              }}
              disabled={!socketReady}
            >
              {isBotResponding ? <BsSendXFill /> : <RiSendPlaneFill />}
            </button>
          </div>
        )}

        <div ref={messagesEndRef} />

        {isModalOpen && (
          <div
            className={`fixed inset-0 flex items-center justify-center z-50 ${themeClasses[currentTheme].modalOverlay}`}
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className={`p-6 rounded-xl w-[400px] shadow-lg relative border ${themeClasses[currentTheme].modalBox}`}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-semibold mb-4 text-center">
                Talk to a Merchant Bay Representative
              </h2>

              <UserInfoForm
                currentTheme={currentTheme}
                onSuccess={(data) => {
                  console.log("Form Submitted:", data);
                }}
              />

              <button
                onClick={() => setIsModalOpen(false)}
                className={`absolute top-1 right-1 ${themeClasses[currentTheme].modalCloseIcon}`}
              >
                <IoIosCloseCircleOutline size={25} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AccordionBotMessage({
  title,
  body,
  isTyping,
  currentTheme,
  setIsModalOpen,
  showRepresentativeButton,
}) {
  const paragraphs = body
    ? body
        .split("\n")
        .map((p) => p.trim())
        .filter((p) => p.length > 0)
    : [];

  return (
    <div className="accordion-message">
      <div
        className={`${themeClasses[currentTheme].messageBody} messageBody border p-[15px_15px_10px_15px] rounded-[10px] mb-2.5`}
      >
        <div className="accordion-bot whitespace-pre-line break-words leading-[1.7em] mt-2">
          {isTyping ? (
            <div className="flex gap-2 items-center">
              <span>Analyzing</span>
              <span className="typing-dots flex gap-1">
                {[...Array(3)].map((_, i) => (
                  <span
                    key={i}
                    className={`w-[5px] h-[5px] rounded-full inline-block animate-bounce ${
                      currentTheme === "light" ? "bg-gray-400" : "bg-white"
                    }`}
                    style={{ animationDelay: `${i * 0.2}s` }}
                  />
                ))}
              </span>
            </div>
          ) : (
            <>
              {paragraphs.map((p, idx) => (
                <ReactMarkdown key={idx}>{p}</ReactMarkdown>
              ))}
            </>
          )}

          {showRepresentativeButton && (
            <div className="flex mt-3 justify-end">
              <div className="relative group inline-block w-auto">
                <span
                  onClick={() => setIsModalOpen(true)}
                  className="chatIconColor cursor-pointer inline-block"
                >
                  <IoChatbubbles size={25} />
                </span>

                <div
                  className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2
          w-max max-w-[300px] bg-gray-800 text-white text-xs p-2 rounded
          opacity-0 group-hover:opacity-100
          pointer-events-none transition-all duration-300"
                >
                  Fill out the form to talk directly with a Merchant Bay
                  representative. We’ll contact you shortly.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
